import { useState } from 'react';

import { Spinner } from '@blueprintjs/core';
import MoreVert from '@material-ui/icons/MoreVert';
import { IconButton, Menu, MenuItem, Select, Tooltip } from '@opendoor/mosaic';

import {
  OdProtosWebData_MaintenanceRequest_State as MaintenanceRequestState,
  OdProtosCommon_DateInput,
} from '../../graphql/athena/types/globalTypes';
import { MaintenanceRequestFields } from '../../graphql/athena/types/MaintenanceRequestFields';
import { MemberFields } from '../../graphql/athena/types/MemberFields';
import { useMarkMaintenanceRequestAsMissed } from '../../graphql/mutations/web/maintenance/useMarkMaintenanceRequestAsMissed';
import { useMarkMaintenanceRequestAsNeedsReview } from '../../graphql/mutations/web/maintenance/useMarkMaintenanceRequestAsNeedsReview';
import { useUpdateMaintenanceRequestScheduledDate } from '../../graphql/mutations/web/maintenance/useUpdateMaintenanceRequestScheduledDate';
import { useUpdateOnsiteWorker } from '../../graphql/mutations/web/maintenance/useUpdateOnsiteWorker';
import useGetOnsiteWorkersForOrg from '../../graphql/queries/organizations/useGetOnsiteWorkersForOrg';
import useListMaintenanceRequests from '../../graphql/queries/web/maintenance/useListMaintenanceRequests';
import {
  AddMaintenanceImageIconUrl,
  GreenCheckCircleIcon,
  MaintenanceImageIconUrl,
} from '../../icons/Icons';
import { prettifyCityStateZip, prettifyStreetAddress } from '../../utils/address';
import {
  dateToProtoDate,
  getWeekdayFromDate,
  protoDateToDate,
  stringDateToProto,
} from '../../utils/date';
import {
  getDueByWeekOfText,
  getFrequencyText,
  getNewScheduledDateForWeekdaySelection,
  prettifyCost,
  prettifyKind,
} from '../../utils/maintenance';
import HorizontalLayout from '../layouts/HorizontalLayout';
import VerticalLayout from '../layouts/VerticalLayout';
import PageChangeButton from '../pagination/PageChangeButton';
import Toaster from '../Toaster';
import MaintenanceRequestDetailModal from './MaintenanceRequestDetailModal';

export enum MaintenanceRequestListFilter {
  OUTSTANDING = 'Outstanding',
  PENDING_OPENDOOR_REVIEW = 'Pending Opendoor Review',
  APPROVED = 'Approved',
  INVOICED = 'Invoiced',
  DENIED = 'Denied',
}

enum MaintenanceRequestTableColumn {
  ADDRESS = 'Address',
  TYPE = 'Type',
  DUE_BY = 'Due By',
  SCHEDULED_DATE = 'Scheduled Date',
  COMPLETION_DATE = 'Completion Date',
  ON_SITE_WORKER = 'On-Site Worker',
  PAYMENT = 'Payment',
  BEFORE = 'Before',
  AFTER = 'After',
  STATUS = 'Status',
  OTHER_ACTIONS = 'Other Actions',
}

interface Column {
  id: MaintenanceRequestTableColumn;
  label: string;
  useValue: (
    request: MaintenanceRequestFields,
    openModal: () => void,
    markAsNeedsReview: [(requestId: string) => Promise<void>, boolean],
    markAsNotDone: [(requestId: string) => Promise<void>, boolean],
    updateScheduledDate: [
      (requestId: string, newDate: OdProtosCommon_DateInput) => Promise<void>,
      boolean,
    ],
    onsiteWorkers: [
      MemberFields[],
      (requestId: string, memberToken: string) => Promise<void>,
      boolean,
    ],
  ) => any;
}

const columns: Column[] = [
  {
    id: MaintenanceRequestTableColumn.ADDRESS,
    label: 'Address',
    useValue: (request, openModal) => {
      if (!request.address) return <div>No Address Found</div>;
      const prettyStreet = prettifyStreetAddress(request.address);
      const prettyCity = prettifyCityStateZip(request.address);

      return (
        <VerticalLayout>
          <div style={styles.streetText} onClick={openModal}>
            {prettyStreet}
          </div>
          <div style={styles.cityStateZipText}>{prettyCity}</div>
        </VerticalLayout>
      );
    },
  },
  {
    id: MaintenanceRequestTableColumn.TYPE,
    label: 'Type',
    useValue: (request) => {
      const kind = prettifyKind(request);
      const frequencyText = getFrequencyText(request);
      return (
        <VerticalLayout>
          <div style={styles.kindText}>{kind}</div>
          <div style={styles.frequencyText}>{frequencyText}</div>
        </VerticalLayout>
      );
    },
  },
  {
    id: MaintenanceRequestTableColumn.DUE_BY,
    label: 'Due By',
    useValue: (request) => {
      const dueByText = getDueByWeekOfText(request);
      return <div>{dueByText}</div>;
    },
  },
  {
    id: MaintenanceRequestTableColumn.SCHEDULED_DATE,
    label: 'Scheduled Date',
    useValue: (request, _, __, ___, updateScheduledDate) => {
      const [update, updateLoading] = updateScheduledDate;
      const currentScheduled = request.scheduledOn;
      let currentWeekdaySelection = null;
      if (currentScheduled) {
        // -1 from month because proto uses Jan = 1 and Date uses Jan = 0
        const currentScheduledDate = protoDateToDate(currentScheduled);
        currentWeekdaySelection = currentScheduledDate.getDay();
      }
      const [currentDropdownValue, setCurrentDropdownValue] = useState(currentWeekdaySelection);
      const handleChange = (selection: number) => {
        if (!currentScheduled) {
          Toaster.show({
            message: `Could not update scheduled date because no date was found.`,
            intent: 'danger',
          });
          return;
        }
        setCurrentDropdownValue(selection);
        const newScheduledDate = getNewScheduledDateForWeekdaySelection(
          currentScheduled,
          selection,
        );
        if (newScheduledDate) {
          update(request.id, dateToProtoDate(newScheduledDate));
        }
      };
      if (updateLoading) {
        return <Spinner />;
      }
      return (
        <Select
          data-testid="weekday-select"
          onChange={(e) => {
            handleChange(e.target.value as number);
          }}
          style={styles.weekdaySelect}
          value={currentDropdownValue}
          defaultValue={currentDropdownValue}
        >
          <MenuItem value={1}>Monday</MenuItem>
          <MenuItem value={2}>Tuesday</MenuItem>
          <MenuItem value={3}>Wednesday</MenuItem>
          <MenuItem value={4}>Thursday</MenuItem>
          <MenuItem value={5}>Friday</MenuItem>
        </Select>
      );
    },
  },
  {
    id: MaintenanceRequestTableColumn.COMPLETION_DATE,
    label: 'Completion Date',
    useValue: (request) => {
      const completedOnDate = request.completedOn ? protoDateToDate(request.completedOn) : null;
      if (completedOnDate) {
        return <span>{getWeekdayFromDate(completedOnDate)}</span>;
      }
      return null;
    },
  },
  {
    id: MaintenanceRequestTableColumn.ON_SITE_WORKER,
    label: 'On-Site Worker',
    useValue: (request, _, __, ___, ____, onsiteWorkers) => {
      const currentOnsiteWorker = request.onsiteWorker || null;
      const members =
        currentOnsiteWorker && currentOnsiteWorker.members ? currentOnsiteWorker.members : [];
      const currentOnsiteMember = members.find((m) =>
        m.roles.find((r) => r.identifier === 'on_site_workers'),
      );
      const currentMemberId = currentOnsiteMember ? currentOnsiteMember.uuid : null;

      const [workers, updateWorker, updateLoading] = onsiteWorkers;
      const [currentDropdownValue, setCurrentDropdownValue] = useState(currentMemberId);
      const handleChange = (selection: string) => {
        if (selection === currentMemberId) return;

        setCurrentDropdownValue(selection);
        updateWorker(request.id, selection);
      };

      if (updateLoading) {
        return <Spinner />;
      }
      return (
        <Select
          data-testid="onsite-worker-select"
          onChange={(e) => {
            handleChange(e.target.value as string);
          }}
          style={styles.weekdaySelect}
          value={currentDropdownValue}
          defaultValue={currentDropdownValue}
        >
          {workers.map((w) => {
            if (w.user) {
              return <MenuItem value={w.uuid}>{w.user.fullName}</MenuItem>;
            }
            return null;
          })}
        </Select>
      );
    },
  },
  {
    id: MaintenanceRequestTableColumn.PAYMENT,
    label: 'Payment',
    useValue: (request) => {
      const costText = prettifyCost(request);
      return <div>{costText}</div>;
    },
  },
  {
    id: MaintenanceRequestTableColumn.BEFORE,
    label: 'Before',
    useValue: (request, openModal) => {
      const beforePhotoCount = request.beforePhotosCount;
      if (beforePhotoCount > 0) {
        return (
          <HorizontalLayout>
            <img
              src={MaintenanceImageIconUrl}
              style={{ cursor: 'pointer', paddingRight: '0.5vw' }}
              alt=""
              onClick={openModal}
              data-testid="before-image-icon"
            />
            <span style={styles.photoCount}>{beforePhotoCount}</span>
          </HorizontalLayout>
        );
      }
      return (
        <img
          src={AddMaintenanceImageIconUrl}
          style={{ cursor: 'pointer' }}
          alt=""
          onClick={openModal}
          data-testid="before-add-image-icon"
        />
      );
    },
  },
  {
    id: MaintenanceRequestTableColumn.AFTER,
    label: 'After',
    useValue: (request, openModal) => {
      const afterPhotosCount = request.afterPhotosCount;
      if (afterPhotosCount > 0) {
        return (
          <HorizontalLayout>
            <img
              src={MaintenanceImageIconUrl}
              style={{ cursor: 'pointer', paddingRight: '0.5vw' }}
              alt=""
              onClick={openModal}
              data-testid="after-image-icon"
            />
            <span style={styles.photoCount}>{afterPhotosCount}</span>
          </HorizontalLayout>
        );
      }
      return (
        <img
          src={AddMaintenanceImageIconUrl}
          style={{ cursor: 'pointer' }}
          alt=""
          onClick={openModal}
          data-testid="after-add-image-icon"
        />
      );
    },
  },
  {
    id: MaintenanceRequestTableColumn.STATUS,
    label: 'Status',
    useValue: (request) => {
      const getStatusBadge = (state: MaintenanceRequestState): JSX.Element => {
        const formattedState = state.charAt(0) + state.slice(1).toLowerCase();
        const baseStyle = {
          height: 30,
          width: 90,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: 4,
          fontWeight: 500,
        };
        switch (state) {
          case MaintenanceRequestState.MISSED:
            return (
              <div style={{ ...baseStyle, backgroundColor: '#FFE6E0', color: '#C34143' }}>
                Missed
              </div>
            );
          case MaintenanceRequestState.APPROVED:
            return (
              <div style={{ ...baseStyle, backgroundColor: '#EAFFFB', color: '#007F5E' }}>
                Approved
              </div>
            );
          case MaintenanceRequestState.INVOICED:
            return (
              <div style={{ ...baseStyle, backgroundColor: '#EAFFFB', color: '#007F5E' }}>
                Invoiced
              </div>
            );
          default:
            return (
              <div style={{ ...baseStyle, backgroundColor: '#EBEFF3', color: '#354C63' }}>
                {formattedState}
              </div>
            );
        }
      };
      return <div data-testid="status-badge">{getStatusBadge(request.state)}</div>;
    },
  },
  {
    id: MaintenanceRequestTableColumn.OTHER_ACTIONS,
    label: 'Other Actions',
    useValue: (request, _, markAsNeedsReview, markAsNotDone) => {
      const [menuAnchorEl, setMenuAnchorEl] = useState(null);
      const [handleCheckClick, markNeedsReviewLoading] = markAsNeedsReview;
      const [handleMarkAsNotDone, markMissedLoading] = markAsNotDone;
      const loading = markNeedsReviewLoading || markMissedLoading;

      if (loading) {
        return <Spinner />;
      }
      return (
        <HorizontalLayout>
          <Tooltip placement={'top'} title="Mark as Complete">
            <img
              src={GreenCheckCircleIcon}
              style={{ cursor: 'pointer' }}
              alt=""
              onClick={() => handleCheckClick(request.id)}
              data-testid="mark-complete-icon-button"
            />
          </Tooltip>
          {request.state !== MaintenanceRequestState.MISSED ? (
            <IconButton
              data-testid="ellipsis-more-icon"
              onClick={(e: any) => setMenuAnchorEl(e.currentTarget)}
            >
              <MoreVert />
            </IconButton>
          ) : null}
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={() => setMenuAnchorEl(null)}
          >
            <MenuItem
              onClick={() => {
                handleMarkAsNotDone(request.id);
                setMenuAnchorEl(null);
              }}
            >
              Mark as Not Done
            </MenuItem>
          </Menu>
        </HorizontalLayout>
      );
    },
  },
];

interface MaintenanceRequestListProps {
  organizationId: string;
  startPeriod: string;
  endPeriod: string;
  filter: MaintenanceRequestListFilter;
  pageSize?: number;
}

const MaintenanceRequestList = ({
  organizationId,
  startPeriod,
  endPeriod,
  filter,
  pageSize = 25,
}: MaintenanceRequestListProps) => {
  const [pageNumber, setPageNumber] = useState(1);

  // this state will also be used to control whether the modal is open or not
  // based on presence of modalRequest
  const [modalRequest, setModalRequest] = useState<MaintenanceRequestFields | null>(null);

  const {
    data,
    loading,
    error: listRequestsError,
    refetch: refetchMaintenanceRequests,
  } = useListMaintenanceRequests({
    variables: {
      orgId: organizationId,
      startPeriod: stringDateToProto(startPeriod),
      endPeriod: stringDateToProto(endPeriod),
      states: getStateParamsForFilter(filter),
      pageSize: pageSize,
      offset: (pageNumber - 1) * pageSize,
    },
  });
  const {
    data: membersData,
    loading: membersLoading,
    error: membersError,
  } = useGetOnsiteWorkersForOrg({
    variables: {
      orgId: organizationId,
    },
  });

  const maintenanceRequests =
    data?.webMaintenance.listMaintenanceRequests?.maintenanceRequests || [];
  const totalCount = data?.webMaintenance.listMaintenanceRequests?.totalCount || 0;
  const showPageButtons = totalCount > pageSize;
  const start = pageNumber * pageSize - (pageSize - 1);
  const end = pageNumber * pageSize - (pageSize - maintenanceRequests.length);
  const onLastPage = pageNumber * pageSize >= totalCount;
  const onsiteWorkers = membersData?.webMaintenance.getOnsiteWorkersForOrg?.members || [];

  const [markAsNeedsReview, { loading: markingAsNeedsReview }] =
    useMarkMaintenanceRequestAsNeedsReview();
  const handleMarkAsNeedsReview = async (requestId: string) => {
    markAsNeedsReview({
      variables: { maintenanceRequestId: requestId },
    })
      .then(() => {
        refetchMaintenanceRequests();
      })
      .catch((e) => {
        Toaster.show({
          message: `There was an error marking request as complete: ${e.message}`,
          intent: 'danger',
        });
      });
  };

  const [markAsMissed, { loading: markingAsMissed }] = useMarkMaintenanceRequestAsMissed();
  const handleMarkAsMissed = async (requestId: string) => {
    markAsMissed({
      variables: { maintenanceRequestId: requestId },
    })
      .then(() => {
        refetchMaintenanceRequests();
      })
      .catch((e) => {
        Toaster.show({
          message: `There was an error marking request as not done: ${e.message}`,
          intent: 'danger',
        });
      });
  };

  const [updateScheduledDate, { loading: updateLoading }] =
    useUpdateMaintenanceRequestScheduledDate();
  const handleUpdateScheduledDate = async (
    requestId: string,
    newDate: OdProtosCommon_DateInput,
  ) => {
    updateScheduledDate({
      variables: { maintenanceRequestId: requestId, newScheduledDate: newDate },
    })
      .then(() => {
        refetchMaintenanceRequests();
      })
      .catch((e) => {
        Toaster.show({
          message: `There was an error updating the scheduled date: ${e.message}`,
          intent: 'danger',
        });
      });
  };

  const [updateOnsiteWorker, { loading: updatingWorker }] = useUpdateOnsiteWorker();
  const handleUpdateOnsiteWorker = async (requestId: string, memberToken: string) => {
    updateOnsiteWorker({
      variables: { maintenanceRequestId: requestId, memberToken: memberToken },
    })
      .then(() => {
        refetchMaintenanceRequests();
      })
      .catch((e) => {
        Toaster.show({
          message: `There was an error updating the onsite worker: ${e.message}`,
          intent: 'danger',
        });
      });
  };

  if (listRequestsError) {
    Toaster.show({
      message: `There was an error fetching maintenance requests: ${listRequestsError.message}`,
      intent: 'danger',
    });
  }
  if (membersError) {
    Toaster.show({
      message: `There was an error fetching onsite workers: ${membersError.message}`,
      intent: 'danger',
    });
  }

  const getCurrentColumns = () => {
    const baseColumns = [
      MaintenanceRequestTableColumn.ADDRESS,
      MaintenanceRequestTableColumn.TYPE,
      MaintenanceRequestTableColumn.DUE_BY,
      MaintenanceRequestTableColumn.ON_SITE_WORKER,
      MaintenanceRequestTableColumn.PAYMENT,
      MaintenanceRequestTableColumn.BEFORE,
      MaintenanceRequestTableColumn.AFTER,
    ];
    const oustandingColumns = [
      ...baseColumns.slice(0, 3),
      MaintenanceRequestTableColumn.SCHEDULED_DATE,
      ...baseColumns.slice(3),
      MaintenanceRequestTableColumn.STATUS,
      MaintenanceRequestTableColumn.OTHER_ACTIONS,
    ];
    const pendingColumns = [
      ...baseColumns.slice(0, 3),
      MaintenanceRequestTableColumn.COMPLETION_DATE,
      ...baseColumns.slice(3),
    ];
    const approvedColumns = [
      ...baseColumns.slice(0, 3),
      MaintenanceRequestTableColumn.COMPLETION_DATE,
      ...baseColumns.slice(3),
      MaintenanceRequestTableColumn.STATUS,
    ];
    const deniedColumns = [
      ...baseColumns.slice(0, 3),
      MaintenanceRequestTableColumn.COMPLETION_DATE,
      ...baseColumns.slice(3),
    ];

    if (filter === MaintenanceRequestListFilter.OUTSTANDING) {
      return columns.filter((c) => oustandingColumns.includes(c.id));
    } else if (filter === MaintenanceRequestListFilter.PENDING_OPENDOOR_REVIEW) {
      return columns.filter((c) => pendingColumns.includes(c.id));
    } else if (
      filter === MaintenanceRequestListFilter.APPROVED ||
      filter === MaintenanceRequestListFilter.INVOICED
    ) {
      return columns.filter((c) => approvedColumns.includes(c.id));
    } else if (filter === MaintenanceRequestListFilter.DENIED) {
      return columns.filter((c) => deniedColumns.includes(c.id));
    } else {
      return columns.filter((c) => baseColumns.includes(c.id));
    }
  };
  const currentColumns = getCurrentColumns();

  if (loading || membersLoading) {
    return <Spinner />;
  }

  return (
    <VerticalLayout style={{ overflowY: 'scroll', height: '100%', width: '100%' }}>
      <table style={{ marginTop: 20, borderCollapse: 'collapse' }}>
        <thead
          style={{ borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: '#C7CFD7' }}
        >
          <tr>
            {currentColumns.map(({ label }, i) => {
              const headerStyle = {
                ...styles.columnHeader,
                ...(i === 0 ? { paddingLeft: 20 } : {}),
              };
              return (
                <th key={i} style={{ ...headerStyle, textAlign: 'left' }}>
                  <span style={styles.columnHeaderText}>{label}</span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {maintenanceRequests.map((request) => (
            <MaintenanceRequestRow
              key={request.id}
              request={request}
              currentColumns={currentColumns}
              openModal={() => setModalRequest(request)}
              markAsNeedsReview={[handleMarkAsNeedsReview, markingAsNeedsReview]}
              markAsMissed={[handleMarkAsMissed, markingAsMissed]}
              updateScheduledDate={[handleUpdateScheduledDate, updateLoading]}
              onsiteWorkers={[onsiteWorkers, handleUpdateOnsiteWorker, updatingWorker]}
            />
          ))}
        </tbody>
      </table>
      {showPageButtons ? (
        <HorizontalLayout style={styles.pagination}>
          <PageChangeButton
            disabled={pageNumber === 1}
            direction="left"
            onClick={() => {
              if (pageNumber !== 1) {
                setPageNumber((currPage) => currPage - 1);
              }
            }}
          />
          <span>{`${start} - ${end}`}</span>
          <PageChangeButton
            disabled={onLastPage}
            direction="right"
            onClick={() => {
              if (!onLastPage) {
                setPageNumber((currPage) => currPage + 1);
              }
            }}
          />
        </HorizontalLayout>
      ) : null}
      <MaintenanceRequestDetailModal
        data-testid="maintenance-request-detail-modal"
        visibleRequest={[modalRequest, setModalRequest]}
        refetchMaintenanceRequests={refetchMaintenanceRequests}
      />
    </VerticalLayout>
  );
};

interface MaintenanceRequestRowProps {
  request: MaintenanceRequestFields;
  currentColumns: Column[];
  openModal: () => void;
  markAsNeedsReview: [(requestId: string) => Promise<void>, boolean];
  markAsMissed: [(requestId: string) => Promise<void>, boolean];
  updateScheduledDate: [
    (requestId: string, newDate: OdProtosCommon_DateInput) => Promise<void>,
    boolean,
  ];
  onsiteWorkers: [
    MemberFields[],
    (requestId: string, memberToken: string) => Promise<void>,
    boolean,
  ];
}

const MaintenanceRequestRow = ({
  request,
  currentColumns,
  openModal,
  markAsNeedsReview,
  markAsMissed,
  updateScheduledDate,
  onsiteWorkers,
}: MaintenanceRequestRowProps) => {
  return (
    <tr style={styles.visitRow} data-testid="maintenance-request-row">
      {currentColumns.map((h, j) => {
        const tdStyle = j === 0 ? { paddingLeft: 20 } : {};
        return (
          <td key={j} style={tdStyle}>
            {h.useValue(
              request,
              openModal,
              markAsNeedsReview,
              markAsMissed,
              updateScheduledDate,
              onsiteWorkers,
            )}
          </td>
        );
      })}
    </tr>
  );
};

const getStateParamsForFilter = (currentColumn: string): MaintenanceRequestState[] => {
  switch (currentColumn) {
    case MaintenanceRequestListFilter.OUTSTANDING:
      return [MaintenanceRequestState.PENDING, MaintenanceRequestState.MISSED];
    case MaintenanceRequestListFilter.PENDING_OPENDOOR_REVIEW:
      return [MaintenanceRequestState.NEEDS_REVIEW];
    case MaintenanceRequestListFilter.APPROVED:
      return [MaintenanceRequestState.APPROVED];
    case MaintenanceRequestListFilter.INVOICED:
      return [MaintenanceRequestState.INVOICED];
    case MaintenanceRequestListFilter.DENIED:
      return [MaintenanceRequestState.DENIED];
    default:
      return [];
  }
};

export default MaintenanceRequestList;

const styles = {
  columnHeader: {
    height: 40,
  },
  columnHeaderText: {
    fontWeight: '600',
    color: '#8D9AA6',
  },
  streetText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#0D76D6',
    cursor: 'pointer',
  },
  cityStateZipText: {
    fontSize: 12,
    fontWeight: '400',
    color: '#6E6E6E',
  },
  kindText: {
    fontWeight: '500',
    fontSize: 14,
    color: '#222222',
  },
  frequencyText: {
    fontWeight: '400',
    fontSize: 12,
    color: '#6E6E6E',
  },
  visitRow: {
    height: 80,
    borderBottom: '1px solid #C7CFD7',
  },
  pagination: {
    width: '100%',
    paddingTop: '2vh',
    marginBottom: '2vh',
    justifyContent: 'flex-end',
  },
  photoCount: {
    fontWeight: '500',
    fontSize: 14,
  },
  weekdaySelect: {
    width: '8vw',
    borderColor: 'black',
  },
};
